import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import {
  downloadAccountDocument,
  type AccountBond,
  type BrokerageBond,
} from "@/lib/brokerAccounts";
import { formatBondDate } from "@/lib/bondValueDisplay";
import { useBondTypeLabels } from "@/lib/useBondTypeLabels";
import { saveFile } from "@/lib/downloadFile";
import { messageFor } from "@/lib/errors";

// The table of ISSUED bonds (BRK-S175), shared since BRK-S181 by the account
// profile's Bonds tab and the Surety nav's brokerage-wide Bonds page — one set
// of rendering decisions for one row, so the two surfaces cannot come to print
// the same bond differently.
//
// The brokerage-wide page adds the INSURED column (`showInsured`); on the
// account tab it would repeat the page's own subject on every row. The
// executed form downloads through the owning ACCOUNT's document route either
// way — "one download door for every broker-visible document" (BRK-S146) —
// which is why the caller says whose account each row is (`accountIdOf`).

type IssuedBond = AccountBond & Partial<Pick<BrokerageBond, "accountName">>;

export function IssuedBondsTable<T extends IssuedBond>({
  bonds,
  accountIdOf,
  showInsured = false,
}: {
  bonds: readonly T[];
  /** Whose account a row belongs to — for its download, and its insured link. */
  accountIdOf: (bond: T) => string;
  showInsured?: boolean;
}) {
  const { authedRequest, authedBlob } = useAuth();
  // `bondType` is the catalogue's KEY, not a label (PMS's own contract note),
  // so it is resolved rather than printed — see `useBondTypeLabels`.
  const bondTypeLabel = useBondTypeLabels(authedRequest);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const download = useCallback(
    async (bond: T) => {
      setBusyId(bond.bondId);
      setDownloadError(null);
      try {
        // The SERVER names the file; the bond number is only the fallback, for
        // a response that carried no `Content-Disposition`.
        // Only ever called for a row that HAS a document — the button is not
        // offered otherwise (see the cell below).
        if (!bond.documentId) return;
        saveFile(
          await downloadAccountDocument(
            authedBlob,
            accountIdOf(bond),
            bond.documentId,
          ),
          `${bond.bondNumber ?? bond.bondId}.pdf`,
        );
      } catch (err) {
        setDownloadError(messageFor(err));
      } finally {
        setBusyId(null);
      }
    },
    [authedBlob, accountIdOf],
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-hidden rounded-xl border border-brk-border bg-brk-panel">
        {/* The table keeps its OWN scroller, so a wide row never pushes the
            page sideways at 375px. */}
        <div className="overflow-x-auto">
          <table
            className={`w-full text-left text-sm ${showInsured ? "min-w-[60rem]" : "min-w-[52rem]"}`}
          >
            <thead className="border-b border-brk-border text-xs font-semibold uppercase tracking-wide text-brk-dim">
              <tr>
                <th className="px-4 py-3">Bond number</th>
                {showInsured && <th className="px-4 py-3">Insured</th>}
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Bond date</th>
                <th className="px-4 py-3">Effective</th>
                <th className="px-4 py-3">Form</th>
                <th className="px-4 py-3">Obligee</th>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">
                  <span className="sr-only">Executed bond</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {bonds.map((bond) => (
                <tr
                  key={bond.bondId}
                  className="border-t border-brk-border transition-colors hover:bg-brk-bg/60"
                >
                  <td className="px-4 py-3 font-mono font-semibold text-brk-text">
                    {/* NULLABLE, and a real state: an issuance whose form maps
                        no number still has a bond. An em dash, never the word
                        "null". */}
                    {bond.bondNumber ?? "—"}
                  </td>
                  {showInsured && (
                    <td className="px-4 py-3">
                      <Link
                        to={`/surety/accounts/${accountIdOf(bond)}`}
                        className="text-brk-accent hover:underline"
                      >
                        {bond.accountName ?? "Account"}
                      </Link>
                    </td>
                  )}
                  {/* The catalogue's KEY resolved to its NAME. Printing it raw
                      would put `bid_bond` on a page a broker hands an obligee
                      (BRK-S179). */}
                  <td className="px-4 py-3 text-brk-text">
                    {bondTypeLabel(bond.bondType)}
                  </td>
                  {/* D-10 rule 2: dates DISPLAY as m/d/yyyy; the wire stays
                      ISO. Parsed textually, so a bond issued on the 1st never
                      renders as the 31st in a western timezone. All three dates
                      are facts an underwriter keyed at prepare time, so a form
                      that maps none of them still has a bond. */}
                  <td className="px-4 py-3 text-brk-dim">
                    {bond.bondDate ? formatBondDate(bond.bondDate) : "—"}
                  </td>
                  <td className="px-4 py-3 text-brk-dim">
                    {bond.effectiveDate
                      ? formatBondDate(bond.effectiveDate)
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-brk-dim">{bond.formTitle}</td>
                  <td className="px-4 py-3 text-brk-dim">
                    {bond.obligeeName ?? "—"}
                  </td>
                  <td className="px-4 py-3">
                    {/* A LINK only when PMS said which order — see
                        `AccountBond.orderId`. Without an id the number still
                        shows, because it is the string a broker reads back over
                        the phone; what it never becomes is a dead link. */}
                    {bond.orderId && bond.orderNumber ? (
                      <Link
                        to={`/surety/submissions/${bond.orderId}`}
                        className="font-mono text-brk-accent hover:underline"
                      >
                        {bond.orderNumber}
                      </Link>
                    ) : (
                      <span className="font-mono text-brk-dim">
                        {bond.orderNumber ?? "—"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {/* WITHHELD when there is no document. PMS publishes
                        `documentId` as null for an envelope that completed
                        before its filing landed — the bond is real and the PDF
                        is not there yet — so a button here could only ever
                        404. The row still lists, and says why there is nothing
                        to press. */}
                    {bond.documentId ? (
                      <button
                        type="button"
                        onClick={() => download(bond)}
                        disabled={busyId === bond.bondId}
                        // NAMED BY ITS ROW. Eight identical "Download" buttons
                        // read as eight identical controls to a screen reader,
                        // which on a page of bonds is the one place it matters
                        // — the broker is fetching a specific obligee's form.
                        aria-label={`Download bond ${bond.bondNumber ?? bond.bondId}`}
                        className="shrink-0 rounded-md border border-brk-border px-3 py-1.5 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {busyId === bond.bondId ? "Preparing…" : "Download"}
                      </button>
                    ) : (
                      <span className="text-xs text-brk-dim">
                        Filing not in yet
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {downloadError && (
        <p role="alert" className="text-sm text-brk-danger">
          {downloadError}
        </p>
      )}
    </div>
  );
}
